/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { CombinedGraphQLErrors, gql, type TypedDocumentNode } from '@apollo/client'
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import { SpinningBorder } from '@repo/shared-client/components/blocks'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@web/src/hooks/use-auth'
import {
  canApplyImageEditorEdits,
  exhaustedImageEditorUsageState,
  IMAGE_EDITOR_FREE_USES_EXHAUSTED_CODE,
  type ImageEditorUsageState,
  imageEditorFreeUsesForRole,
} from '@web/src/lib/graphql/medias/image-editor-usage'
import { useTranslation } from '@web/src/lib/intl/react'
import {
  BlendIcon,
  CropIcon,
  FlipHorizontal2Icon,
  Grid2x2Icon,
  Maximize2Icon,
  RotateCwIcon,
  SlidersHorizontalIcon,
  StickerIcon,
  XIcon,
} from 'lucide-react'
import {
  type ReactElement,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import toast from 'react-hot-toast'
import type {
  ConsumeImageEditorUseMutation,
  ConsumeImageEditorUseMutationVariables,
  ImageEditorUsageQuery,
  ImageEditorUsageQueryVariables,
} from 'types/graphql'
import { canvasToBlob, exportEditedImage, loadHtmlImage, renderOrientedImage } from './image-editor-canvas'
import ImageEditorCoverPreview from './image-editor-cover-preview'
import ImageEditorCropFrame from './image-editor-crop-frame'
import {
  clampCrop,
  clampOverlay,
  clientPointToNormalized,
  fitCropToAspect,
  getContainedRect,
  getCropPixelAspect,
  getOrientationCssTransform,
  getOrientedSize,
  moveCrop,
  remapOverlaysForCrop,
  remapOverlaysForFlipX,
  remapOverlaysForRotateCw,
  resizeCropFromCorner,
} from './image-editor-geometry'
import ImageEditorOverlayFrame from './image-editor-overlay-frame'
import {
  COVER_HEIGHT_RATIO,
  COVER_WIDTH_RATIO,
  type CoverKind,
  type CoverShape,
  type CropAspect,
  type CropHandle,
  type CropRect,
  DEFAULT_PRIVACY_EMOJI,
  type EditorCover,
  type EditorSticker,
  type EditorTool,
  EMOJI_SIZE_RATIO,
  FILE_EXTENSION_PATTERN,
  FULL_CROP,
  getPhotoFilterCss,
  type ImageRect,
  MAX_OVERLAY_HEIGHT_RATIO,
  MAX_OVERLAY_WIDTH_RATIO,
  MIN_OVERLAY_HEIGHT_RATIO,
  MIN_OVERLAY_WIDTH_RATIO,
  ORIENTED_PREVIEW_MAX_DIMENSION,
  type OverlayLayer,
  PHOTO_FILTERS,
  type PhotoFilter,
  PRIVACY_EMOJIS,
  STICKER_SIZE_RATIO,
  type StickerKind,
} from './image-editor-types'
import flamingoStickerUrl from './stickers/flamingo.webp'
import logoStickerUrl from './stickers/logo-plain.svg'
import pineappleStickerUrl from './stickers/pineapple.webp'
import swingerStickerUrl from './stickers/swinger.svg'

const STICKER_ASPECT: Record<StickerKind, number> = {
  logo: 141 / 110,
  pineapple: 839 / 512,
  flamingo: 988 / 512,
  swinger: 1,
}

const STICKER_SRC: Record<StickerKind, string> = {
  logo: logoStickerUrl,
  pineapple: pineappleStickerUrl,
  flamingo: flamingoStickerUrl,
  swinger: swingerStickerUrl,
}

const CROP_ASPECTS: CropAspect[] = ['free', '1:1', '4:5', '16:9']

const IMAGE_EDITOR_USAGE_QUERY: TypedDocumentNode<ImageEditorUsageQuery, ImageEditorUsageQueryVariables> = gql`
  query ImageEditorUsageQuery {
    imageEditorUsage {
      usedCount
      freeUses
      remainingUses
      unlimited
    }
  }
`

const CONSUME_IMAGE_EDITOR_USE: TypedDocumentNode<
  ConsumeImageEditorUseMutation,
  ConsumeImageEditorUseMutationVariables
> = gql`
  mutation ConsumeImageEditorUse {
    consumeImageEditorUse {
      usedCount
      freeUses
      remainingUses
      unlimited
    }
  }
`

const isImageEditorUsesExhaustedError = (error: unknown) =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some((graphQlError) => graphQlError.extensions?.code === IMAGE_EDITOR_FREE_USES_EXHAUSTED_CODE)

const readAsDataUrl = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
        return
      }
      reject(new Error('Failed to load image'))
    })
    reader.addEventListener('error', () => {
      reject(reader.error ?? new Error('Failed to load image'))
    })
    reader.readAsDataURL(file)
  })

const previewUrlForFile = (file: File) => {
  const preview = (file as File & { preview?: unknown }).preview
  return typeof preview === 'string' ? preview : null
}

const MenuTooltip = ({ className, label, trigger }: { className?: string; label: string; trigger: ReactElement }) => (
  <div className={cn('inline-flex shrink-0', className)}>
    <Tooltip>
      <TooltipTrigger className="inline-flex w-full" render={<span />}>
        {trigger}
      </TooltipTrigger>
      <TooltipContent positionerClassName="z-[100]">{label}</TooltipContent>
    </Tooltip>
  </div>
)

const ToolOptionRow = ({ children }: { children: ReactNode }) => (
  <div className="min-w-0 overflow-x-auto overscroll-x-contain pb-1">
    <div className="flex w-max min-w-full items-start justify-center gap-2">{children}</div>
  </div>
)

type PointerSession =
  | {
      type: 'drag'
      layer: OverlayLayer
      overlayId: string
      startX: number
      startY: number
      originX: number
      originY: number
    }
  | {
      type: 'resize'
      layer: OverlayLayer
      overlayId: string
      lockAspect: boolean
      startWidth: number
      startHeight: number
      originX: number
      originY: number
      imageWidth: number
      imageHeight: number
    }
  | {
      type: 'rotate'
      layer: OverlayLayer
      overlayId: string
      startRotation: number
      originAngle: number
      centerX: number
      centerY: number
    }
  | {
      type: 'crop-move'
      startX: number
      startY: number
      width: number
      height: number
      originX: number
      originY: number
    }
  | {
      type: 'crop-resize'
      handle: CropHandle
      startCrop: CropRect
      aspect: number | null
      orientedWidth: number
      orientedHeight: number
    }

type ImageEditorDialogProps = {
  file: File | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onApply: (editedFile: File) => void | Promise<void>
}

type SelectedOverlay = {
  layer: OverlayLayer
  id: string
}

const ImageEditorDialog = ({ file, open, onOpenChange, onApply }: ImageEditorDialogProps) => {
  const { t } = useTranslation()
  const { currentUserRole, hasPermission } = useAuth()
  const apolloClient = useApolloClient()
  const navigate = useNavigate()
  const titleId = useId()
  const stageRef = useRef<HTMLDivElement>(null)
  const pointerSessionRef = useRef<PointerSession | null>(null)
  const handlePointerMoveRef = useRef<(event: PointerEvent) => void>(() => undefined)
  const endPointerSessionRef = useRef<() => void>(() => undefined)
  const orientedPreviewUrlRef = useRef<string | null>(null)
  const isApplyingRef = useRef(false)
  const tRef = useRef(t)
  tRef.current = t
  const hasUnlimitedImageEdits = hasPermission('media:edit-image')
  const {
    data: imageEditorUsageData,
    error: imageEditorUsageError,
    loading: imageEditorUsageLoading,
    refetch: refetchImageEditorUsage,
  } = useQuery(IMAGE_EDITOR_USAGE_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !(open && !hasUnlimitedImageEdits),
  })
  const [consumeImageEditorUse] = useMutation(CONSUME_IMAGE_EDITOR_USE, {
    update(cache, { data }) {
      if (!data?.consumeImageEditorUse) {
        return
      }
      cache.writeQuery({
        data: { imageEditorUsage: data.consumeImageEditorUse },
        query: IMAGE_EDITOR_USAGE_QUERY,
      })
    },
  })
  const imageEditorUsage = imageEditorUsageData?.imageEditorUsage
  const imageEditorFreeUses = imageEditorUsage?.freeUses ?? imageEditorFreeUsesForRole(currentUserRole)
  const isUsageLoading = !hasUnlimitedImageEdits && imageEditorUsageLoading && !imageEditorUsage

  const writeImageEditorUsage = (usage: ImageEditorUsageState) => {
    apolloClient.writeQuery({
      data: { imageEditorUsage: usage },
      query: IMAGE_EDITOR_USAGE_QUERY,
    })
  }

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [orientedPreviewUrl, setOrientedPreviewUrl] = useState<string | null>(null)
  const [orientedPreviewSize, setOrientedPreviewSize] = useState({ width: 0, height: 0 })
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 })
  const [imageRect, setImageRect] = useState<ImageRect>({ left: 0, top: 0, width: 0, height: 0 })
  const [photoFilter, setPhotoFilter] = useState<PhotoFilter>('original')
  const [rotation, setRotation] = useState(0)
  const [flipX, setFlipX] = useState(false)
  const [crop, setCrop] = useState<CropRect>(FULL_CROP)
  const [draftCrop, setDraftCrop] = useState<CropRect>(FULL_CROP)
  const [cropAspect, setCropAspect] = useState<CropAspect>('free')
  const [tool, setTool] = useState<EditorTool | null>(null)
  const [stickers, setStickers] = useState<EditorSticker[]>([])
  const [covers, setCovers] = useState<EditorCover[]>([])
  const [selectedOverlay, setSelectedOverlay] = useState<SelectedOverlay | null>(null)
  const [isApplying, setIsApplying] = useState(false)
  const [showImageEditorUpgrade, setShowImageEditorUpgrade] = useState(false)
  const [imageLoadError, setImageLoadError] = useState(false)

  const isCropMode = tool === 'crop'
  const usesSourcePreview = rotation === 0 && !flipX
  const sourceOrientedSize = getOrientedSize(naturalSize.width, naturalSize.height, rotation)
  const hasBakedOrientedPreview = Boolean(!usesSourcePreview && orientedPreviewUrl && orientedPreviewSize.width > 0)
  const displayUrl = hasBakedOrientedPreview ? orientedPreviewUrl : imageUrl
  const displayOrientedSize = hasBakedOrientedPreview ? orientedPreviewSize : sourceOrientedSize
  const useCssOrientation = Boolean(displayUrl && !usesSourcePreview && !hasBakedOrientedPreview)
  const activeCrop = isCropMode ? draftCrop : crop
  const viewSize = isCropMode
    ? displayOrientedSize
    : {
        width: crop.width * displayOrientedSize.width,
        height: crop.height * displayOrientedSize.height,
      }
  const photoScale = viewSize.width > 0 ? imageRect.width / viewSize.width : 0
  const orientedDisplayWidth = displayOrientedSize.width * photoScale
  const orientedDisplayHeight = displayOrientedSize.height * photoScale
  const photoOffsetX = isCropMode ? 0 : -crop.x * orientedDisplayWidth
  const photoOffsetY = isCropMode ? 0 : -crop.y * orientedDisplayHeight

  useEffect(() => {
    if (!(open && file)) {
      setImageUrl(null)
      return
    }

    let cancelled = false
    const objectUrl = URL.createObjectURL(file)
    setImageUrl(objectUrl)
    setPhotoFilter('original')
    setRotation(0)
    setFlipX(false)
    setCrop(FULL_CROP)
    setDraftCrop(FULL_CROP)
    setCropAspect('free')
    setTool(null)
    setStickers([])
    setCovers([])
    setSelectedOverlay(null)
    setNaturalSize({ width: 0, height: 0 })
    setOrientedPreviewUrl(null)
    setOrientedPreviewSize({ width: 0, height: 0 })
    setIsApplying(false)
    setShowImageEditorUpgrade(false)
    setImageLoadError(false)

    const applyLoadedImage = (image: HTMLImageElement, src: string) => {
      if (cancelled) {
        return
      }
      setImageUrl(src)
      setNaturalSize({ width: image.naturalWidth, height: image.naturalHeight })
    }

    const loadEditorImage = async () => {
      try {
        applyLoadedImage(await loadHtmlImage(objectUrl), objectUrl)
        return
      } catch {
        // The blob URL can fail for a file the thumbnail already decoded. Retry
        // through a data URL, then the thumbnail itself, before reporting an error.
      }

      if (cancelled) {
        return
      }

      try {
        const dataUrl = await readAsDataUrl(file)
        if (cancelled) {
          return
        }
        applyLoadedImage(await loadHtmlImage(dataUrl), dataUrl)
        return
      } catch {
        // Fall through to the thumbnail the uploader is already showing.
      }

      const previewUrl = previewUrlForFile(file)
      if (previewUrl) {
        try {
          applyLoadedImage(await loadHtmlImage(previewUrl), previewUrl)
          return
        } catch {
          // The thumbnail is unavailable too.
        }
      }

      if (cancelled) {
        return
      }
      setImageLoadError(true)
      toast.error(tRef.current('Failed to load image'))
    }

    loadEditorImage()

    return () => {
      cancelled = true
      URL.revokeObjectURL(objectUrl)
    }
  }, [open, file])

  useEffect(() => {
    const revokeOrientedPreview = () => {
      if (orientedPreviewUrlRef.current) {
        URL.revokeObjectURL(orientedPreviewUrlRef.current)
        orientedPreviewUrlRef.current = null
      }
    }

    if (!imageUrl || naturalSize.width === 0 || usesSourcePreview) {
      revokeOrientedPreview()
      setOrientedPreviewUrl(null)
      setOrientedPreviewSize({ width: 0, height: 0 })
      return
    }

    let cancelled = false

    const renderPreview = async () => {
      const photo = await loadHtmlImage(imageUrl)
      if (cancelled) {
        return
      }

      const canvas = renderOrientedImage(photo, rotation, flipX, ORIENTED_PREVIEW_MAX_DIMENSION)
      const blob = await canvasToBlob(canvas)
      if (cancelled) {
        return
      }

      const nextUrl = URL.createObjectURL(blob)
      if (cancelled) {
        URL.revokeObjectURL(nextUrl)
        return
      }

      revokeOrientedPreview()
      orientedPreviewUrlRef.current = nextUrl
      setOrientedPreviewUrl(nextUrl)
      setOrientedPreviewSize({ width: canvas.width, height: canvas.height })
    }

    renderPreview().catch((error) => {
      if (cancelled) {
        return
      }
      console.error(error)
      toast.error(t('Failed to render image preview'))
    })

    return () => {
      cancelled = true
    }
  }, [flipX, imageUrl, naturalSize.width, rotation, t, usesSourcePreview])

  useEffect(() => {
    return () => {
      if (orientedPreviewUrlRef.current) {
        URL.revokeObjectURL(orientedPreviewUrlRef.current)
        orientedPreviewUrlRef.current = null
      }
    }
  }, [])

  const updateImageRect = useCallback(() => {
    const stage = stageRef.current
    if (!stage || viewSize.width === 0 || viewSize.height === 0) {
      return
    }

    setImageRect(getContainedRect(viewSize.width, viewSize.height, stage.clientWidth, stage.clientHeight))
  }, [viewSize.height, viewSize.width])

  useLayoutEffect(() => {
    if (!open) {
      return
    }

    updateImageRect()

    const stage = stageRef.current
    if (!stage) {
      return
    }

    const observer = new ResizeObserver(() => {
      updateImageRect()
    })
    observer.observe(stage)
    return () => observer.disconnect()
  }, [open, updateImageRect, displayUrl, isCropMode])

  const commitCrop = (nextCrop: CropRect) => {
    const clamped = clampCrop(nextCrop)
    setStickers((current) => remapOverlaysForCrop(current, crop, clamped))
    setCovers((current) => remapOverlaysForCrop(current, crop, clamped))
    setCrop(clamped)
    setDraftCrop(clamped)
  }

  const selectTool = (nextTool: EditorTool | null) => {
    if (tool === 'crop' && nextTool !== 'crop') {
      commitCrop(draftCrop)
    }
    if (nextTool === 'crop') {
      setDraftCrop(crop)
      setSelectedOverlay(null)
    }
    setTool(nextTool)
  }

  const closeToolSheet = () => {
    selectTool(null)
  }

  const applyCropAspect = (aspect: CropAspect) => {
    setCropAspect(aspect)
    const pixelAspect = getCropPixelAspect(aspect)
    if (!pixelAspect) {
      return
    }
    setDraftCrop(fitCropToAspect(draftCrop, pixelAspect, sourceOrientedSize.width, sourceOrientedSize.height))
  }

  const resetCrop = () => {
    const pixelAspect = getCropPixelAspect(cropAspect)
    setDraftCrop(
      pixelAspect
        ? fitCropToAspect(FULL_CROP, pixelAspect, sourceOrientedSize.width, sourceOrientedSize.height)
        : FULL_CROP
    )
  }

  const rotateImage = () => {
    const currentCrop = isCropMode ? clampCrop(draftCrop) : crop
    const nextStickers = remapOverlaysForRotateCw(
      stickers,
      currentCrop,
      sourceOrientedSize.width,
      sourceOrientedSize.height
    )
    const nextCovers = remapOverlaysForRotateCw(
      covers,
      currentCrop,
      sourceOrientedSize.width,
      sourceOrientedSize.height
    )
    setStickers(nextStickers.overlays)
    setCovers(nextCovers.overlays)
    setCrop(nextStickers.crop)
    setDraftCrop(nextStickers.crop)
    setCropAspect((current) => (current === '1:1' || current === 'free' ? current : 'free'))
    setRotation((current) => (current + 90) % 360)
    setSelectedOverlay(null)
  }

  const flipImage = () => {
    const currentCrop = isCropMode ? clampCrop(draftCrop) : crop
    const nextStickers = remapOverlaysForFlipX(stickers, currentCrop)
    const nextCovers = remapOverlaysForFlipX(covers, currentCrop)
    setStickers(nextStickers.overlays.map((sticker) => ({ ...sticker, flipX: !sticker.flipX })))
    setCovers(nextCovers.overlays)
    setCrop(nextStickers.crop)
    setDraftCrop(nextStickers.crop)
    setFlipX((current) => !current)
    setSelectedOverlay(null)
  }

  const addSticker = (kind: StickerKind) => {
    const workingWidth = crop.width * sourceOrientedSize.width
    const workingHeight = crop.height * sourceOrientedSize.height
    if (workingWidth <= 0 || workingHeight <= 0) {
      return
    }

    const shorterSide = Math.min(workingWidth, workingHeight)
    const widthPx = shorterSide * STICKER_SIZE_RATIO
    const heightPx = widthPx * STICKER_ASPECT[kind]
    const nextSticker = clampOverlay({
      id: crypto.randomUUID(),
      kind,
      flipX: false,
      rotation: 0,
      width: widthPx / workingWidth,
      height: heightPx / workingHeight,
      x: (workingWidth - widthPx) / 2 / workingWidth,
      y: (workingHeight - heightPx) / 2 / workingHeight,
    })

    setStickers((current) => [...current, nextSticker])
    setSelectedOverlay({ layer: 'sticker', id: nextSticker.id })
  }

  const addCover = (kind: CoverKind, emoji: string = DEFAULT_PRIVACY_EMOJI) => {
    const workingWidth = crop.width * sourceOrientedSize.width
    const workingHeight = crop.height * sourceOrientedSize.height
    if (workingWidth <= 0 || workingHeight <= 0) {
      return
    }

    const shorterSide = Math.min(workingWidth, workingHeight)
    const widthPx = shorterSide * (kind === 'emoji' ? EMOJI_SIZE_RATIO : COVER_WIDTH_RATIO)
    const heightPx = kind === 'emoji' ? widthPx : shorterSide * COVER_HEIGHT_RATIO
    const nextCover = clampOverlay({
      id: crypto.randomUUID(),
      kind,
      shape: 'ellipse' as CoverShape,
      emoji,
      rotation: 0,
      width: widthPx / workingWidth,
      height: heightPx / workingHeight,
      x: (workingWidth - widthPx) / 2 / workingWidth,
      y: (workingHeight - heightPx) / 2 / workingHeight,
    })

    setCovers((current) => [...current, nextCover])
    setSelectedOverlay({ layer: 'cover', id: nextCover.id })
  }

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const session = pointerSessionRef.current
      if (!session) {
        return
      }

      event.preventDefault()

      if (session.type === 'crop-move') {
        if (imageRect.width === 0 || imageRect.height === 0) {
          return
        }
        const dx = (event.clientX - session.originX) / imageRect.width
        const dy = (event.clientY - session.originY) / imageRect.height
        setDraftCrop(
          moveCrop(
            {
              x: session.startX,
              y: session.startY,
              width: session.width,
              height: session.height,
            },
            dx,
            dy
          )
        )
        return
      }

      if (session.type === 'crop-resize') {
        const stageBox = stageRef.current?.getBoundingClientRect()
        if (!stageBox) {
          return
        }
        const point = clientPointToNormalized(event.clientX, event.clientY, stageBox, imageRect)
        setDraftCrop(
          resizeCropFromCorner(
            session.startCrop,
            session.handle,
            point.x,
            point.y,
            session.aspect,
            session.orientedWidth,
            session.orientedHeight
          )
        )
        return
      }

      const applyToLayer = session.layer === 'sticker' ? setStickers : setCovers
      applyToLayer((current) =>
        current.map((overlay) => {
          if (overlay.id !== session.overlayId) {
            return overlay
          }

          if (session.type === 'drag') {
            if (imageRect.width === 0 || imageRect.height === 0) {
              return overlay
            }

            const dx = event.clientX - session.originX
            const dy = event.clientY - session.originY
            return clampOverlay({
              ...overlay,
              x: session.startX + dx / imageRect.width,
              y: session.startY + dy / imageRect.height,
            })
          }

          if (session.type === 'rotate') {
            const nextAngle = Math.atan2(event.clientY - session.centerY, event.clientX - session.centerX)
            const deltaDegrees = ((nextAngle - session.originAngle) * 180) / Math.PI
            return {
              ...overlay,
              rotation: session.startRotation + deltaDegrees,
            }
          }

          const dx = event.clientX - session.originX
          const dy = event.clientY - session.originY
          if (session.imageWidth <= 0 || session.imageHeight <= 0) {
            return overlay
          }

          if (!session.lockAspect) {
            return clampOverlay({
              ...overlay,
              width: session.startWidth + dx / session.imageWidth,
              height: session.startHeight + dy / session.imageHeight,
            })
          }

          const startWidthPx = session.startWidth * session.imageWidth
          const startHeightPx = session.startHeight * session.imageHeight
          if (startWidthPx <= 0) {
            return overlay
          }

          const pixelAspect = startHeightPx / startWidthPx
          let nextWidthPx = Math.max(1, startWidthPx + dx)
          let nextHeightPx = nextWidthPx * pixelAspect
          const minWidthPx = MIN_OVERLAY_WIDTH_RATIO * session.imageWidth
          const maxWidthPx = MAX_OVERLAY_WIDTH_RATIO * session.imageWidth
          const minHeightPx = MIN_OVERLAY_HEIGHT_RATIO * session.imageHeight
          const maxHeightPx = MAX_OVERLAY_HEIGHT_RATIO * session.imageHeight
          nextWidthPx = Math.min(maxWidthPx, Math.max(minWidthPx, nextWidthPx))
          nextHeightPx = nextWidthPx * pixelAspect

          if (nextHeightPx > maxHeightPx) {
            nextHeightPx = maxHeightPx
            nextWidthPx = nextHeightPx / pixelAspect
          } else if (nextHeightPx < minHeightPx) {
            nextHeightPx = minHeightPx
            nextWidthPx = nextHeightPx / pixelAspect
          }

          return clampOverlay({
            ...overlay,
            width: nextWidthPx / session.imageWidth,
            height: nextHeightPx / session.imageHeight,
          })
        })
      )
    },
    [imageRect.height, imageRect.left, imageRect.top, imageRect.width]
  )

  handlePointerMoveRef.current = handlePointerMove

  const onWindowPointerMove = useCallback((event: PointerEvent) => {
    handlePointerMoveRef.current(event)
  }, [])

  const onWindowPointerUp = useCallback(() => {
    endPointerSessionRef.current()
  }, [])

  const endPointerSession = useCallback(() => {
    pointerSessionRef.current = null
    window.removeEventListener('pointermove', onWindowPointerMove)
    window.removeEventListener('pointerup', onWindowPointerUp)
    window.removeEventListener('pointercancel', onWindowPointerUp)
  }, [onWindowPointerMove, onWindowPointerUp])

  endPointerSessionRef.current = endPointerSession

  const beginPointerSession = (session: PointerSession, event?: ReactPointerEvent) => {
    pointerSessionRef.current = session
    event?.currentTarget.setPointerCapture(event.pointerId)
    window.addEventListener('pointermove', onWindowPointerMove)
    window.addEventListener('pointerup', onWindowPointerUp)
    window.addEventListener('pointercancel', onWindowPointerUp)
  }

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onWindowPointerMove)
      window.removeEventListener('pointerup', onWindowPointerUp)
      window.removeEventListener('pointercancel', onWindowPointerUp)
    }
  }, [onWindowPointerMove, onWindowPointerUp])

  const beginOverlayDrag = (event: ReactPointerEvent, layer: OverlayLayer, overlay: EditorSticker | EditorCover) => {
    event.stopPropagation()
    event.preventDefault()
    setSelectedOverlay({ layer, id: overlay.id })
    beginPointerSession(
      {
        type: 'drag',
        layer,
        overlayId: overlay.id,
        startX: overlay.x,
        startY: overlay.y,
        originX: event.clientX,
        originY: event.clientY,
      },
      event
    )
  }

  const beginOverlayRotate = (event: ReactPointerEvent, layer: OverlayLayer, overlay: EditorSticker | EditorCover) => {
    event.stopPropagation()
    event.preventDefault()
    const stageBox = stageRef.current?.getBoundingClientRect()
    const centerX = (stageBox?.left ?? 0) + imageRect.left + (overlay.x + overlay.width / 2) * imageRect.width
    const centerY = (stageBox?.top ?? 0) + imageRect.top + (overlay.y + overlay.height / 2) * imageRect.height
    beginPointerSession(
      {
        type: 'rotate',
        layer,
        overlayId: overlay.id,
        startRotation: overlay.rotation,
        originAngle: Math.atan2(event.clientY - centerY, event.clientX - centerX),
        centerX,
        centerY,
      },
      event
    )
  }

  const beginOverlayResize = (
    event: ReactPointerEvent,
    layer: OverlayLayer,
    overlay: EditorSticker | EditorCover,
    lockAspect: boolean
  ) => {
    event.stopPropagation()
    event.preventDefault()
    beginPointerSession(
      {
        type: 'resize',
        layer,
        overlayId: overlay.id,
        lockAspect,
        startWidth: overlay.width,
        startHeight: overlay.height,
        originX: event.clientX,
        originY: event.clientY,
        imageWidth: imageRect.width,
        imageHeight: imageRect.height,
      },
      event
    )
  }

  const handleApply = async () => {
    if (isApplyingRef.current || isUsageLoading) {
      return
    }

    let usage = imageEditorUsage

    if (!hasUnlimitedImageEdits && imageEditorUsageError && !usage) {
      try {
        const result = await refetchImageEditorUsage()
        usage = result.data?.imageEditorUsage
        if (result.error || !usage) {
          toast.error(t('Failed to apply image edits'))
          return
        }
      } catch (error) {
        console.error(error)
        toast.error(t('Failed to apply image edits'))
        return
      }
    }

    if (!(hasUnlimitedImageEdits || canApplyImageEditorEdits(usage))) {
      if (usage) {
        writeImageEditorUsage(exhaustedImageEditorUsageState(usage))
      }
      setShowImageEditorUpgrade(true)
      return
    }

    if (!(file && imageUrl) || naturalSize.width === 0) {
      return
    }

    isApplyingRef.current = true
    setIsApplying(true)

    try {
      const exportCrop = isCropMode ? clampCrop(draftCrop) : crop
      const exportStickers = isCropMode ? remapOverlaysForCrop(stickers, crop, exportCrop) : stickers
      const exportCovers = isCropMode ? remapOverlaysForCrop(covers, crop, exportCrop) : covers
      const blob = await exportEditedImage({
        sourceUrl: imageUrl,
        rotation,
        flipX,
        crop: exportCrop,
        photoFilter,
        stickers: exportStickers,
        covers: exportCovers,
        stickerSources: STICKER_SRC,
      })

      const baseName = file.name.replace(FILE_EXTENSION_PATTERN, '')
      await onApply(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }))

      if (!hasUnlimitedImageEdits) {
        try {
          await consumeImageEditorUse()
        } catch (error) {
          if (isImageEditorUsesExhaustedError(error)) {
            writeImageEditorUsage(exhaustedImageEditorUsageState(usage))
          } else {
            console.error(error)
          }
        }
      }

      onOpenChange(false)
    } catch (error) {
      console.error(error)
      toast.error(t('Failed to apply image edits'))
    } finally {
      isApplyingRef.current = false
      setIsApplying(false)
    }
  }

  const handleClose = () => {
    if (isApplyingRef.current || isApplying) {
      return
    }
    onOpenChange(false)
  }

  const canTransform = Boolean(imageUrl && naturalSize.width > 0)
  const canEditOverlays = Boolean(canTransform && displayUrl && imageRect.width > 0 && !isCropMode)

  const overlayStyle = (overlay: EditorSticker | EditorCover) => ({
    left: overlay.x * imageRect.width,
    top: overlay.y * imageRect.height,
    width: overlay.width * imageRect.width,
    height: overlay.height * imageRect.height,
    transform: `rotate(${overlay.rotation}deg)`,
    transformOrigin: 'center center',
  })

  const renderCoverVisual = (cover: EditorCover, isSelected: boolean) => {
    if (cover.kind === 'emoji') {
      return (
        <div
          className={cn(
            'flex size-full select-none items-center justify-center leading-none',
            isSelected && 'outline outline-2 outline-primary outline-offset-1'
          )}
          style={{
            fontSize: Math.min(cover.width * imageRect.width, cover.height * imageRect.height) * 0.85,
          }}
        >
          {cover.emoji}
        </div>
      )
    }

    const coverLeft = cover.x * imageRect.width
    const coverTop = cover.y * imageRect.height
    const clipClass = 'rounded-full'

    if (!displayUrl) {
      return <div className={cn('size-full bg-black/20', clipClass)} />
    }

    return (
      <ImageEditorCoverPreview
        clipClass={clipClass}
        coverHeight={cover.height * imageRect.height}
        coverLeft={coverLeft}
        coverRotation={cover.rotation}
        coverTop={coverTop}
        coverWidth={cover.width * imageRect.width}
        displayUrl={displayUrl}
        isSelected={isSelected}
        kind={cover.kind}
        orientedDisplayHeight={orientedDisplayHeight}
        orientedDisplayWidth={orientedDisplayWidth}
        photoFilter={photoFilter}
        photoOffsetX={photoOffsetX}
        photoOffsetY={photoOffsetY}
        viewHeight={imageRect.height}
        viewWidth={imageRect.width}
      />
    )
  }

  const photoFilterLabel = (filter: PhotoFilter) => {
    if (filter === 'mono') {
      return t('Mono')
    }
    if (filter === 'warm') {
      return t('Warm')
    }
    if (filter === 'cool') {
      return t('Cool')
    }
    if (filter === 'fade') {
      return t('Fade')
    }
    return t('Original')
  }

  const cropAspectLabel = (aspect: CropAspect) => {
    if (aspect === 'free') {
      return t('Free')
    }
    return aspect
  }

  const toolTitle = (nextTool: EditorTool) => {
    if (nextTool === 'adjust') {
      return t('Adjust')
    }
    if (nextTool === 'crop') {
      return t('Crop')
    }
    if (nextTool === 'stickers') {
      return t('Stickers')
    }
    return t('Blur')
  }

  return (
    <>
      <Dialog
        disablePointerDismissal
        modal
        onOpenChange={(nextOpen) => !(isApplyingRef.current || isApplying) && onOpenChange(nextOpen)}
        open={open}
      >
        <DialogContent
          aria-describedby={undefined}
          aria-labelledby={titleId}
          className="translate-none top-0 right-0 bottom-0 left-0 z-70 flex h-auto max-h-none min-h-0 w-auto max-w-none flex-col gap-0 overflow-hidden rounded-none bg-background p-0 ring-0 sm:max-w-none"
          initialFocus={false}
          showCloseButton={false}
        >
          <header className="relative z-10 flex min-w-0 shrink-0 items-center gap-1.5 border-b bg-background px-2 py-2">
            <DialogTitle className="min-w-0 flex-1 truncate" id={titleId}>
              {t('Edit image')}
            </DialogTitle>
            <DialogDescription className="sr-only">{t('Edit image')}</DialogDescription>
            {imageEditorUsage && !imageEditorUsage.unlimited && imageEditorUsage.remainingUses > 0 ? (
              <SpinningBorder className="inline-block shrink-0 px-2" continuous>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {imageEditorUsage.remainingUses === 1
                    ? t('1 free use left')
                    : t('{{count}} free uses left', { count: imageEditorUsage.remainingUses })}
                </p>
              </SpinningBorder>
            ) : null}
            <Button disabled={isApplying} onClick={handleClose} size="sm" type="button" variant="outline">
              {t('Cancel')}
            </Button>
            <Button
              disabled={!canTransform || isApplying || isUsageLoading}
              onClick={() => {
                handleApply()
              }}
              size="sm"
              type="button"
            >
              {isApplying ? <Spinner className="size-4" /> : t('Save')}
            </Button>
          </header>

          <div
            className="relative min-h-0 flex-1 touch-none overflow-hidden bg-black"
            onPointerDown={() => setSelectedOverlay(null)}
            ref={stageRef}
          >
            {imageLoadError ? (
              <div className="flex size-full items-center justify-center px-4">
                <p className="text-center text-white">{t('Failed to load image')}</p>
              </div>
            ) : displayUrl ? (
              <div
                className="absolute overflow-hidden"
                style={{
                  left: imageRect.left,
                  top: imageRect.top,
                  width: imageRect.width || 1,
                  height: imageRect.height || 1,
                }}
              >
                {useCssOrientation ? (
                  <div
                    className="pointer-events-none absolute overflow-hidden"
                    style={{
                      left: photoOffsetX,
                      top: photoOffsetY,
                      width: orientedDisplayWidth || 1,
                      height: orientedDisplayHeight || 1,
                    }}
                  >
                    <img
                      alt=""
                      className="absolute max-w-none select-none"
                      draggable={false}
                      height={naturalSize.height * photoScale || 1}
                      src={displayUrl}
                      style={{
                        left: '50%',
                        top: '50%',
                        width: naturalSize.width * photoScale || 1,
                        height: naturalSize.height * photoScale || 1,
                        transform: `translate(-50%, -50%) ${getOrientationCssTransform(rotation, flipX)}`,
                        filter: getPhotoFilterCss(photoFilter),
                      }}
                      width={naturalSize.width * photoScale || 1}
                    />
                  </div>
                ) : (
                  <img
                    alt=""
                    className="pointer-events-none absolute max-w-none select-none"
                    draggable={false}
                    height={orientedDisplayHeight || 1}
                    src={displayUrl}
                    style={{
                      left: photoOffsetX,
                      top: photoOffsetY,
                      width: orientedDisplayWidth || 1,
                      height: orientedDisplayHeight || 1,
                      filter: getPhotoFilterCss(photoFilter),
                    }}
                    width={orientedDisplayWidth || 1}
                  />
                )}
                {canEditOverlays
                  ? covers.map((cover) => {
                      const isSelected = selectedOverlay?.layer === 'cover' && selectedOverlay.id === cover.id
                      return (
                        <ImageEditorOverlayFrame
                          key={cover.id}
                          onDragPointerDown={(event) => beginOverlayDrag(event, 'cover', cover)}
                          onRemove={() => {
                            setCovers((current) => current.filter((item) => item.id !== cover.id))
                            setSelectedOverlay(null)
                          }}
                          onResizePointerDown={(event) =>
                            beginOverlayResize(event, 'cover', cover, cover.kind === 'emoji')
                          }
                          onRotatePointerDown={(event) => beginOverlayRotate(event, 'cover', cover)}
                          removeLabel={t('Remove cover')}
                          resizeLabel={t('Resize cover')}
                          rotateLabel={t('Rotate cover')}
                          selected={isSelected}
                          style={overlayStyle(cover)}
                        >
                          {renderCoverVisual(cover, isSelected)}
                        </ImageEditorOverlayFrame>
                      )
                    })
                  : null}
                {canEditOverlays
                  ? stickers.map((sticker) => {
                      const isSelected = selectedOverlay?.layer === 'sticker' && selectedOverlay.id === sticker.id
                      return (
                        <ImageEditorOverlayFrame
                          key={sticker.id}
                          onDragPointerDown={(event) => beginOverlayDrag(event, 'sticker', sticker)}
                          onRemove={() => {
                            setStickers((current) => current.filter((item) => item.id !== sticker.id))
                            setSelectedOverlay(null)
                          }}
                          onResizePointerDown={(event) => beginOverlayResize(event, 'sticker', sticker, true)}
                          onRotatePointerDown={(event) => beginOverlayRotate(event, 'sticker', sticker)}
                          removeLabel={t('Remove sticker')}
                          resizeLabel={t('Resize sticker')}
                          rotateLabel={t('Rotate sticker')}
                          selected={isSelected}
                          style={overlayStyle(sticker)}
                        >
                          <img
                            alt=""
                            className={cn(
                              'size-full select-none',
                              isSelected && 'outline outline-2 outline-primary outline-offset-1'
                            )}
                            draggable={false}
                            height={sticker.height * imageRect.height}
                            src={STICKER_SRC[sticker.kind]}
                            style={{ transform: sticker.flipX ? 'scaleX(-1)' : undefined }}
                            width={sticker.width * imageRect.width}
                          />
                        </ImageEditorOverlayFrame>
                      )
                    })
                  : null}
                {isCropMode && imageRect.width > 0 ? (
                  <ImageEditorCropFrame
                    crop={activeCrop}
                    imageRect={imageRect}
                    moveLabel={t('Move crop')}
                    onMovePointerDown={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      beginPointerSession(
                        {
                          type: 'crop-move',
                          startX: draftCrop.x,
                          startY: draftCrop.y,
                          width: draftCrop.width,
                          height: draftCrop.height,
                          originX: event.clientX,
                          originY: event.clientY,
                        },
                        event
                      )
                    }}
                    onResizePointerDown={(handle, event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      beginPointerSession(
                        {
                          type: 'crop-resize',
                          handle,
                          startCrop: draftCrop,
                          aspect: getCropPixelAspect(cropAspect),
                          orientedWidth: sourceOrientedSize.width,
                          orientedHeight: sourceOrientedSize.height,
                        },
                        event
                      )
                    }}
                    resizeLabel={t('Resize crop')}
                  />
                ) : null}
              </div>
            ) : (
              <div className="flex size-full items-center justify-center">
                <Spinner className="size-8 text-white" />
              </div>
            )}
          </div>

          <footer className="relative z-10 min-w-0 shrink-0 border-t bg-background px-2 py-2">
            {tool === null ? (
              <div className="grid grid-cols-4 gap-1">
                <MenuTooltip
                  className="w-full"
                  label={t('Adjust')}
                  trigger={
                    <Button
                      className="h-16 w-full flex-col items-center gap-1"
                      onClick={() => selectTool('adjust')}
                      type="button"
                      variant="ghost"
                    >
                      <SlidersHorizontalIcon className="size-6" />
                      {t('Adjust')}
                    </Button>
                  }
                />
                <MenuTooltip
                  className="w-full"
                  label={t('Crop')}
                  trigger={
                    <Button
                      className="h-16 w-full flex-col items-center gap-1"
                      onClick={() => selectTool('crop')}
                      type="button"
                      variant="ghost"
                    >
                      <CropIcon className="size-6" />
                      {t('Crop')}
                    </Button>
                  }
                />
                <MenuTooltip
                  className="w-full"
                  label={t('Stickers')}
                  trigger={
                    <Button
                      className="h-16 w-full flex-col items-center gap-1"
                      onClick={() => selectTool('stickers')}
                      type="button"
                      variant="ghost"
                    >
                      <StickerIcon className="size-6" />
                      {t('Stickers')}
                    </Button>
                  }
                />
                <MenuTooltip
                  className="w-full"
                  label={t('Blur')}
                  trigger={
                    <Button
                      className="h-16 w-full flex-col items-center gap-1"
                      onClick={() => selectTool('blur')}
                      type="button"
                      variant="ghost"
                    >
                      <BlendIcon className="size-6" />
                      {t('Blur')}
                    </Button>
                  }
                />
              </div>
            ) : (
              <div className="flex flex-col gap-3 pt-1">
                <div className="relative flex min-h-8 items-center justify-center">
                  <p className="font-medium">{toolTitle(tool)}</p>
                  <MenuTooltip
                    label={t('Close')}
                    trigger={
                      <Button
                        aria-label={t('Close')}
                        className="absolute top-0 right-0"
                        onClick={closeToolSheet}
                        size="icon-sm"
                        type="button"
                        variant="ghost"
                      >
                        <XIcon />
                      </Button>
                    }
                  />
                </div>

                {tool === 'adjust' ? (
                  <ToolOptionRow>
                    {PHOTO_FILTERS.map((filter) => (
                      <div className="flex shrink-0 flex-col items-center gap-1" key={filter}>
                        <MenuTooltip
                          label={photoFilterLabel(filter)}
                          trigger={
                            <Button
                              aria-label={photoFilterLabel(filter)}
                              aria-pressed={photoFilter === filter}
                              className="size-12 overflow-hidden p-1"
                              onClick={() => setPhotoFilter(filter)}
                              type="button"
                              variant={photoFilter === filter ? 'default' : 'outline'}
                            >
                              {displayUrl ? (
                                <img
                                  alt=""
                                  className="size-full object-cover"
                                  height={48}
                                  src={displayUrl}
                                  style={{ filter: getPhotoFilterCss(filter) }}
                                  width={48}
                                />
                              ) : (
                                <span
                                  aria-hidden
                                  className="size-full rounded-sm bg-[linear-gradient(135deg,#525252_25%,#d4d4d4_25%,#d4d4d4_50%,#525252_50%,#525252_75%,#d4d4d4_75%)] bg-size-[8px_8px]"
                                  style={{ filter: getPhotoFilterCss(filter) }}
                                />
                              )}
                            </Button>
                          }
                        />
                        <span className="text-muted-foreground text-xs">{photoFilterLabel(filter)}</span>
                      </div>
                    ))}
                    <div className="flex shrink-0 flex-col items-center gap-1">
                      <MenuTooltip
                        label={t('Rotate')}
                        trigger={
                          <Button
                            aria-label={t('Rotate')}
                            className="size-12"
                            disabled={!canTransform}
                            onClick={rotateImage}
                            type="button"
                            variant="outline"
                          >
                            <RotateCwIcon className="size-6" />
                          </Button>
                        }
                      />
                      <span className="text-muted-foreground text-xs">{t('Rotate')}</span>
                    </div>
                    <div className="flex shrink-0 flex-col items-center gap-1">
                      <MenuTooltip
                        label={t('Flip')}
                        trigger={
                          <Button
                            aria-label={t('Flip')}
                            className="size-12"
                            disabled={!canTransform}
                            onClick={flipImage}
                            type="button"
                            variant="outline"
                          >
                            <FlipHorizontal2Icon className="size-6" />
                          </Button>
                        }
                      />
                      <span className="text-muted-foreground text-xs">{t('Flip')}</span>
                    </div>
                  </ToolOptionRow>
                ) : null}

                {tool === 'crop' ? (
                  <ToolOptionRow>
                    {CROP_ASPECTS.map((aspect) => (
                      <MenuTooltip
                        key={aspect}
                        label={cropAspectLabel(aspect)}
                        trigger={
                          <Button
                            aria-label={cropAspectLabel(aspect)}
                            aria-pressed={cropAspect === aspect}
                            className="size-12 shrink-0 px-1"
                            onClick={() => applyCropAspect(aspect)}
                            type="button"
                            variant={cropAspect === aspect ? 'default' : 'outline'}
                          >
                            {cropAspectLabel(aspect)}
                          </Button>
                        }
                      />
                    ))}
                    <MenuTooltip
                      label={t('Reset crop')}
                      trigger={
                        <Button
                          aria-label={t('Reset crop')}
                          className="size-12 shrink-0"
                          onClick={resetCrop}
                          type="button"
                          variant="outline"
                        >
                          <Maximize2Icon />
                        </Button>
                      }
                    />
                  </ToolOptionRow>
                ) : null}

                {tool === 'stickers' ? (
                  <ToolOptionRow>
                    <MenuTooltip
                      label={t('Add logo')}
                      trigger={
                        <Button
                          aria-label={t('Add logo')}
                          className="size-12 shrink-0 overflow-hidden p-1"
                          disabled={!canEditOverlays}
                          onClick={() => addSticker('logo')}
                          type="button"
                          variant="outline"
                        >
                          <img
                            alt=""
                            className="size-full object-contain"
                            height={141}
                            src={logoStickerUrl}
                            width={110}
                          />
                        </Button>
                      }
                    />
                    <MenuTooltip
                      label={t('Add pineapple')}
                      trigger={
                        <Button
                          aria-label={t('Add pineapple')}
                          className="size-12 shrink-0 overflow-hidden p-1"
                          disabled={!canEditOverlays}
                          onClick={() => addSticker('pineapple')}
                          type="button"
                          variant="outline"
                        >
                          <img
                            alt=""
                            className="size-full object-contain"
                            height={839}
                            src={pineappleStickerUrl}
                            width={512}
                          />
                        </Button>
                      }
                    />
                    <MenuTooltip
                      label={t('Add flamingo')}
                      trigger={
                        <Button
                          aria-label={t('Add flamingo')}
                          className="size-12 shrink-0 overflow-hidden p-1"
                          disabled={!canEditOverlays}
                          onClick={() => addSticker('flamingo')}
                          type="button"
                          variant="outline"
                        >
                          <img
                            alt=""
                            className="size-full object-contain"
                            height={988}
                            src={flamingoStickerUrl}
                            width={512}
                          />
                        </Button>
                      }
                    />
                    <MenuTooltip
                      label={t('Add swinger symbol')}
                      trigger={
                        <Button
                          aria-label={t('Add swinger symbol')}
                          className="size-12 shrink-0 overflow-hidden p-1"
                          disabled={!canEditOverlays}
                          onClick={() => addSticker('swinger')}
                          type="button"
                          variant="outline"
                        >
                          <img
                            alt=""
                            className="size-full object-contain"
                            height={200}
                            src={swingerStickerUrl}
                            width={200}
                          />
                        </Button>
                      }
                    />
                    {PRIVACY_EMOJIS.map((emoji) => (
                      <MenuTooltip
                        key={emoji}
                        label={emoji}
                        trigger={
                          <Button
                            aria-label={emoji}
                            className="size-12 shrink-0 overflow-hidden p-1 text-4xl leading-none"
                            disabled={!canEditOverlays}
                            onClick={() => addCover('emoji', emoji)}
                            type="button"
                            variant="outline"
                          >
                            {emoji}
                          </Button>
                        }
                      />
                    ))}
                  </ToolOptionRow>
                ) : null}

                {tool === 'blur' ? (
                  <ToolOptionRow>
                    <div className="flex shrink-0 flex-col items-center gap-1">
                      <MenuTooltip
                        label={t('Blur')}
                        trigger={
                          <Button
                            aria-label={t('Add blur cover')}
                            className="size-12"
                            disabled={!canEditOverlays}
                            onClick={() => addCover('blur')}
                            type="button"
                            variant="outline"
                          >
                            <BlendIcon className="size-6" />
                          </Button>
                        }
                      />
                      <span className="text-muted-foreground text-xs">{t('Blur')}</span>
                    </div>
                    <div className="flex shrink-0 flex-col items-center gap-1">
                      <MenuTooltip
                        label={t('Pixelate')}
                        trigger={
                          <Button
                            aria-label={t('Add pixelate cover')}
                            className="size-12"
                            disabled={!canEditOverlays}
                            onClick={() => addCover('pixelate')}
                            type="button"
                            variant="outline"
                          >
                            <Grid2x2Icon className="size-6" />
                          </Button>
                        }
                      />
                      <span className="text-muted-foreground text-xs">{t('Pixelate')}</span>
                    </div>
                  </ToolOptionRow>
                ) : null}
              </div>
            )}
          </footer>
        </DialogContent>
      </Dialog>
      <Dialog onOpenChange={setShowImageEditorUpgrade} open={showImageEditorUpgrade}>
        <DialogContent className="z-80" initialFocus={false} showCloseButton>
          <DialogHeader>
            <DialogTitle>{t('Want to apply these edits?')}</DialogTitle>
            <DialogDescription>
              {t('You have used your {{count}} free image editor saves. Become a supporter for unlimited edits.', {
                count: imageEditorFreeUses,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowImageEditorUpgrade(false)
                navigate({ to: '/supporter' })
              }}
              type="button"
            >
              {t('Become a Supporter')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ImageEditorDialog
